import React, { forwardRef } from "react";

import { Link, LinkProps } from "@UI/Components/Link/Link";

import { LinkProps as ReactRouterLinkProps } from "react-router-dom";

import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import {
  IGenerateRouteKey,
  IGenerateRouteParams,
  IGenerateRouteQueryParams,
} from "@Libs/Router/Contexts/RoutesContext";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";

type ExcludedReactRouterLinkProps = "css" | "color" | "to" | "type";

export interface RouterLinkProps
  extends LinkProps,
    Omit<ReactRouterLinkProps, ExcludedReactRouterLinkProps> {
  route?: IGenerateRouteKey;
  params?: IGenerateRouteParams;
  query?: IGenerateRouteQueryParams;
  isNavLink?: boolean;
}

// eslint-disable-next-line react/display-name
export const RouterLink = forwardRef<HTMLAnchorElement, RouterLinkProps>(
  (
    { route, params, query, onClick, as: As, reloadDocument, ...props },
    ref
  ) => {
    const { generateRoute } = useRoutes();
    const navigate = useNavigate();

    const path = generateRoute(route, params, query);

    const handleClick: RouterLinkProps["onClick"] = (e) => {
      e.preventDefault();
      onClick && onClick(e);

      if (route) {
        navigate(route, {
          reload: reloadDocument,
          params,
          queryParams: query,
        });
      }
    };

    const commonProps = {
      ...props,
      cursor: "pointer",
      ref,
    };

    if (As && As !== "a") {
      return <As as={Link} onClick={handleClick} {...commonProps} />;
    } else if (As && As === "a") {
      return (
        <Link
          as={As}
          href={path}
          onClick={(e) => onClick?.(e)}
          {...commonProps}
        />
      );
    }

    return <Link href={path} onClick={(e) => onClick?.(e)} {...commonProps} />;
  }
);
